import styled, {
  css,
  Interpolation,
  SimpleInterpolation,
  ThemedStyledProps,
  ThemeProps,
} from 'styled-components';
import { updateThemeColumns } from '../../../helpers/columns';
import { ColumnsByBreakpoint } from '../../../helpers/columns/types';
import { ComponentProps } from '../../../helpers/componentProps';
import { stylePerBreakpoint } from '../../../helpers/mediaQuery';
import { Breakpoint, Theme } from '../../../theme';

export type GridItemProps = Partial<ColumnsByBreakpoint> & {focusable?: boolean;} & ComponentProps;

export const calculateGridItemWidth = (
  breakpoint: Breakpoint,
  theme: Theme,
  gridItemProps: GridItemProps
): SimpleInterpolation => {
  const parentWidthInColumns = theme.layout[breakpoint].columns;
  const itemColumns = gridItemProps[breakpoint] ?? parentWidthInColumns;
  const { gutterPx } = theme.layout[breakpoint];
  return `calc(${(itemColumns / parentWidthInColumns) * 100}% - ${gutterPx}px)`;
};

/**
 * This function allows the overriding of grid item props on a GridItem component.
 * @param gridItemProps - the number of columns for the grid item to span for each breakpoint
 * @param parentGridItemProps (optional) - the number of columns spanned for each breakpoint by the targeted grid item's closest grid item ancestor
 *
 * Note that if no `parentGridItemProps` value is passed in for a breakpoint, the theme used will be from the root component where this function is used.
 */
export const gridItemPropsOverride = (
  gridItemProps: GridItemProps,
  parentGridItemProps?: GridItemProps
): Interpolation<ThemeProps<Theme>> =>
  stylePerBreakpoint((breakpoint) => ({ theme }) => {
    const updatedTheme = parentGridItemProps
      ? updateThemeColumns(theme, parentGridItemProps)
      : theme;
    return css`
      width: ${calculateGridItemWidth(breakpoint, updatedTheme, gridItemProps)};
    `;
  });

export const GridItemWrapper = styled.div<Partial<ColumnsByBreakpoint>>`
  flex: 0 0 auto;

  ${stylePerBreakpoint(
    (breakpoint) => (
      props: ThemedStyledProps<Partial<ColumnsByBreakpoint>, Theme>
    ) => css`
      margin: 0 ${props.theme.layout[breakpoint].gutterPx / 2}px;
      width: ${calculateGridItemWidth(breakpoint, props.theme, props)};
    `
  )}
`;
